/* ==================== 1. STATIC IMPORTS (MUST BE AT TOP) ==================== */
import React, { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import IdleSignout from "./utils/idleSignout";
import { auth } from "./firebaseConfig";
import HomePageLayout from "./layouts/HomePageLayout";
import RequireRoles from "./pages/private/RequireRoles";
import ErrorBoundary from "./components/ErrorBoundary";
import AdminSubdomainGuard, { isAdminSubdomain, isEbooksSubdomain } from "./components/AdminSubdomainGuard";
import { recordPageVisit, handleGlobalClick } from "./utils/navigationTracker";
import { logPermissionDenied } from "./utils/errorLogger";
import { AI_PM_PRICING } from "./config/subscriptionConfig";
import VisitorTracker from "./components/VisitorTracker";
import PageTransition from "./components/PageTransition";
import HavloController from "./components/HavloController";
import CourseReviewFeedback from "./components/tester/CourseReviewFeedback";
import LiveChatWidget from "./components/LiveChat/LiveChatWidget";
import HumanRequestAlert from "./components/admin/HumanRequestAlert";

/** Redirect AI PM course slugs away from /learn/dev/ to /academy/my/ai-for-pms/ */
function DevPlayerGuard({ children }) {
  const { slug, moduleKey } = useParams();
  const isAIPM = AI_PM_PRICING.allCourseSlugs.includes(slug) ||
    slug === AI_PM_PRICING.bundleSlug ||
    slug === AI_PM_PRICING.bundleProductSlug;
  if (isAIPM) {
    const modNum = moduleKey ? parseInt(moduleKey.replace("module-", ""), 10) : 0;
    const target = modNum > 0
      ? `/academy/my/ai-for-pms/${slug}/module/${modNum}`
      : `/academy/my/ai-for-pms/${slug}`;
    return <Navigate to={target} replace />;
  }
  return children;
}

/** Legacy redirect: /ai-for-project-managers/:slug → /academy/ai-for-pms/:slug */
function RedirectOldAIPMDetail() {
  const { slug } = useParams();
  return <Navigate to={`/academy/ai-for-pms/${slug}`} replace />;
}

/** Legacy learner redirect helpers */
// Old certificate links (/certificate-preview?id=...) go to the public verification page.
function CertificatePreviewRedirect() {
  const id = new URLSearchParams(window.location.search).get("id");
  return <Navigate to={id ? `/verify/${encodeURIComponent(id)}` : "/learner/my-exams"} replace />;
}
function RedirectPromptModule() { const { moduleId } = useParams(); return <Navigate to={`/academy/my/prompt-engineering/${moduleId}`} replace />; }
function RedirectAIDeliveryModule() { const { moduleId } = useParams(); return <Navigate to={`/academy/my/ai-systems-delivery/${moduleId}`} replace />; }
function RedirectAIPMCourse() { const { slug } = useParams(); return <Navigate to={`/academy/my/ai-for-pms/${slug}`} replace />; }
function RedirectAIPMIntro() { const { slug } = useParams(); return <Navigate to={`/academy/my/ai-for-pms/${slug}/introduction`} replace />; }
function RedirectAIPMModuleRedir() { const { slug, moduleIndex } = useParams(); return <Navigate to={`/academy/my/ai-for-pms/${slug}/module/${moduleIndex}`} replace />; }
function RedirectAIPMCert() { const { slug } = useParams(); return <Navigate to={`/academy/my/ai-for-pms/${slug}/certificate`} replace />; }

/* AI for PMs bundle dashboard — lazy loaded */
const AIPMBundlePage = lazy(() => import("./pages/learner/AIPMBundlePage"));

/* PWA Components — lazy-loaded to avoid pulling notification/Firebase deps into main bundle */
const InstallBanner = lazy(() => import("./pwa/InstallBanner"));
const OfflineBanner = lazy(() => import("./pwa/OfflineBanner"));

/* ==================== 2. LAZY LOADED COMPONENTS (ALPHABETICAL) ==================== */

/* LAYOUTS */
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const SpecialistLayout = lazy(() => import("./layouts/SpecialistLayout"));

/* PUBLIC */
const AboutMe = lazy(() => import("./pages/public/AboutMe"));
const MeetYourInstructors = lazy(() => import("./pages/public/MeetYourInstructors"));
const ArticlesPage = lazy(() => import("./pages/public/ArticlesPage"));
const AITransformingPM = lazy(() => import("./pages/public/articles/AITransformingPM"));
const TeachingAISkillsToPMs = lazy(() => import("./pages/public/articles/TeachingAISkillsToPMs"));
const FutureOfProjectLeadership = lazy(() => import("./pages/public/articles/FutureOfProjectLeadership"));
const TopAIToolsForPMs = lazy(() => import("./pages/public/articles/TopAIToolsForPMs"));
const WhyPMsMustDevelopAISkills = lazy(() => import("./pages/public/articles/WhyPMsMustDevelopAISkills"));
const AIRiskManagementForPMs = lazy(() => import("./pages/public/articles/AIRiskManagementForPMs"));
const AIAgileProjectDelivery = lazy(() => import("./pages/public/articles/AIAgileProjectDelivery"));
const AIStakeholderManagement = lazy(() => import("./pages/public/articles/AIStakeholderManagement"));
const BuildingAIReadyPMO = lazy(() => import("./pages/public/articles/BuildingAIReadyPMO"));
const AIDataDrivenDecisionsPM = lazy(() => import("./pages/public/articles/AIDataDrivenDecisionsPM"));
const AIChangeManagement = lazy(() => import("./pages/public/articles/AIChangeManagement"));
const AIProjectBudgeting = lazy(() => import("./pages/public/articles/AIProjectBudgeting"));
const AIQualityManagement = lazy(() => import("./pages/public/articles/AIQualityManagement"));
const AIResourceManagement = lazy(() => import("./pages/public/articles/AIResourceManagement"));
const AIAutomationForProjectManagers = lazy(() => import("./pages/public/articles/AIAutomationForProjectManagers"));
const PMBOK8ExamChanges = lazy(() => import("./pages/public/articles/PMBOK8ExamChanges"));
const DeliverablesSuiteAIDocumentGenerator = lazy(() => import("./pages/public/articles/DeliverablesSuiteAIDocumentGenerator"));
const TheScope180ProjectDashboard = lazy(() => import("./pages/public/articles/TheScope180ProjectDashboard"));
const TheScope180AcademyOnlinePMCourses = lazy(() => import("./pages/public/articles/TheScope180AcademyOnlinePMCourses"));
const LivePMPStudyGroupsExamPrep = lazy(() => import("./pages/public/articles/LivePMPStudyGroupsExamPrep"));
const AuthAction = lazy(() => import("./pages/auth/AuthAction"));
const BlogPage = lazy(() => import("./pages/public/BlogPage"));
const BlogPostDetail = lazy(() => import("./pages/public/BlogPostDetail"));
const CertificateVerify = lazy(() => import("./pages/public/CertificateVerify"));
const CareersPage = lazy(() => import("./pages/public/CareersPage"));
const ContactPage = lazy(() => import("./pages/public/ContactPage"));
const SocialsPage = lazy(() => import("./pages/public/SocialsPage"));
const SpeakingPage = lazy(() => import("./pages/public/SpeakingPage"));
const SkillBuildersPage = lazy(() => import("./pages/public/SkillBuildersPage"));
const AIProductLeadershipSP = lazy(() => import("./pages/public/AIProductLeadershipSP"));
const AIForPMsLanding = lazy(() => import("./pages/public/AIForPMsLanding"));
const AIPMCourseDetail = lazy(() => import("./pages/public/AIPMCourseDetail"));
// CPMAISimulator (src/pages/public/simulators/CPMAISimulator.jsx) retired Nov 4 2026 (owner
// decision): source kept, route redirects to the Learning Resources page (/products).
// CPMAINextSteps (same folder) retired with it; source kept.
const HomePage = lazy(() => import("./pages/public/HomePage"));
const LiveCourseCatalog = lazy(() => import("./pages/public/LiveCourseCatalog"));
const NotFound = lazy(() => import("./pages/public/NotFound"));
const OhSTPMToolkit = lazy(() => import("./pages/samples/OhSTPMToolkit"));
const PathfinderSummary = lazy(() => import("./pages/public/PathfinderSummary"));
const PMCertificationPathfinder = lazy(() => import("./pages/public/PMCertificationPathfinder"));
const PMQuiz = lazy(() => import("./pages/public/PMQuiz"));
// PracticeExams removed — content moved to /products?tab=exams
const PrivacyPolicy = lazy(() => import("./pages/public/PrivacyPolicy"));
const ProductDetailPage = lazy(() => import("./pages/public/ProductDetailPage"));
const RefundPolicy = lazy(() => import("./pages/public/RefundPolicy"));
const ProductsPage = lazy(() => import("./pages/public/products"));
const RecommendedTraining = lazy(() => import("./pages/public/RecommendedTraining"));
const SampleViewer = lazy(() => import("./pages/public/SampleViewer"));
const Terms = lazy(() => import("./pages/public/Terms"));
const UnauthorizedPage = lazy(() => import("./pages/public/UnauthorizedPage"));
const Waitlist = lazy(() => import("./pages/public/waitlist/Waitlist"));
const WaitlistLogin = lazy(() => import("./pages/public/waitlist/waitlistlogin"));
const WaitlistThanks = lazy(() => import("./pages/public/waitlist/Thanks"));
const WorkSamples = lazy(() => import("./pages/public/WorkSamples"));

/* LEARNER — ACADEMY */
const AcademyDashboard = lazy(() => import("./pages/learner/AcademyDashboard"));

/* AUTH / CHECKOUT */
const CheckoutGate = lazy(() => import("./pages/checkout/CheckoutGate"));
const EmailConfirmedPage = lazy(() => import("./pages/auth/EmailConfirmedPage"));
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const EnterpriseSSOLogin = lazy(() => import("./pages/auth/EnterpriseSSOLogin"));
const PayForProduct = lazy(() => import("./pages/checkout/PayForProduct"));
const PostLoginRedirect = lazy(() => import("./pages/private/PostLoginRedirect"));
const PurchaseSuccess = lazy(() => import("./pages/checkout/PurchaseSuccess"));
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));
const VerifyEmailPage = lazy(() => import("./pages/auth/VerifyEmailPage"));
const WelcomePage = lazy(() => import("./pages/WelcomePage"));

/* PROJECT DASHBOARD (STANDALONE PRODUCT) */
const ProjectDashboardLayout = lazy(() => import("./layouts/ProjectDashboardLayout"));
const ProjectDashboardLanding = lazy(() => import("./pages/product/project-dashboard/ProjectDashboardLanding"));
const ProjectDashboardAuth = lazy(() => import("./pages/product/project-dashboard/ProjectDashboardAuth"));
const ProjectPortfolio = lazy(() => import("./pages/product/project-dashboard/ProjectPortfolio"));
const ProjectDashboardApp = lazy(() => import("./pages/product/project-dashboard/ProjectDashboard"));
const SharedProjectView = lazy(() => import("./pages/product/project-dashboard/SharedProjectView"));

/* SUBSCRIPTION */
const PricingPage = lazy(() => import("./pages/subscription/PricingPage"));
const SubscribePage = lazy(() => import("./pages/subscription/SubscribePage"));
const NewsletterSignup = lazy(() => import("./pages/public/NewsletterSignup"));
const SubscriptionSuccess = lazy(() => import("./pages/subscription/SubscriptionSuccess"));
const ManageSubscription = lazy(() => import("./pages/subscription/ManageSubscription"));

/* ADMIN */
const AddClientIntakeForm = lazy(() => import("./pages/admin/intake/AddClientIntakeForm"));
const AdminClientDashboard = lazy(() => import("./pages/admin/clients/AdminClientDashboard"));
const UserManagement = lazy(() => import("./pages/admin/users/UserManagement"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminInbox = lazy(() => import("./pages/admin/messages/AdminInbox"));
const ClientDetail = lazy(() => import("./pages/admin/clients/clientdetail"));

const EmailSettings = lazy(() => import("./pages/admin/email/EmailSettings"));
const Employees = lazy(() => import("./pages/admin/employees/Employees"));
const ExamBulkUploader = lazy(() => import("./pages/admin/exams/ExamBulkUploader"));
const FinancesPage = lazy(() => import("./pages/admin/finances/FinancesPage"));
const SiteStatistics = lazy(() => import("./pages/admin/statistics/SiteStatistics"));
const LearnerActivity = lazy(() => import("./pages/admin/statistics/LearnerActivity"));
const Pmbok8TimeLog = lazy(() => import("./pages/admin/statistics/Pmbok8TimeLog"));
const YouTubePublisher = lazy(() => import("./pages/admin/youtube/YouTubePublisher"));
const StudioIntroPreview = lazy(() => import("./pages/admin/StudioIntroPreview"));
const PartnersPage = lazy(() => import("./pages/admin/partners/PartnersPage"));
const PartnerDetailPage = lazy(() => import("./pages/admin/partners/PartnerDetailPage"));
const OutreachPage = lazy(() => import("./pages/admin/partners/OutreachPage"));
const OpportunitiesPage = lazy(() => import("./pages/admin/partners/OpportunitiesPage"));
const WorkBoardPage = lazy(() => import("./pages/admin/partners/WorkBoardPage"));
const SamStatusPage = lazy(() => import("./pages/admin/partners/SamStatusPage"));
const SamScanPage = lazy(() => import("./pages/admin/partners/SamScanPage"));
const InboxPage = lazy(() => import("./pages/admin/partners/InboxPage"));
const NdaCountersignPage = lazy(() => import("./pages/admin/partners/NdaCountersignPage"));
const ContractsDashboard = lazy(() => import("./pages/admin/partners/ContractsDashboard"));


const MarketingHub = lazy(() => import("./pages/admin/marketing/MarketingHub"));
const MarketingDashboard = lazy(() => import("./pages/admin/marketing/MarketingDashboard"));
const EmailMarketing = lazy(() => import("./pages/admin/marketing/EmailMarketing"));
const MarketingEmails = lazy(() => import("./pages/admin/marketing/MarketingEmails"));
const MarketingLanding = lazy(() => import("./pages/admin/marketing/MarketingLanding"));
const MarketingLeads = lazy(() => import("./pages/admin/marketing/MarketingLeads"));
const NewsletterSubscribers = lazy(() => import("./pages/admin/marketing/NewsletterSubscribers"));
const NewsletterComposer = lazy(() => import("./pages/admin/marketing/NewsletterComposer"));
const NewsletterHub = lazy(() => import("./pages/admin/marketing/NewsletterHub"));
const NewsletterAutomation = lazy(() => import("./pages/admin/marketing/NewsletterAutomation"));
const ContentCalendar = lazy(() => import("./pages/admin/marketing/ContentCalendar"));
const NewsletterStudio = lazy(() => import("./pages/admin/marketing/NewsletterStudio"));
const AssetsManager = lazy(() => import("./pages/admin/marketing/AssetsManager"));
const MarketingBlog = lazy(() => import("./pages/admin/marketing/BlogManager"));
const SEOManager = lazy(() => import("./pages/admin/marketing/SEOManager"));
const AnalyticsDashboard = lazy(() => import("./pages/admin/marketing/AnalyticsDashboard"));
const SocialMedia = lazy(() => import("./pages/admin/marketing/SocialMedia"));
const Promotions = lazy(() => import("./pages/admin/marketing/Promotions"));
const AnnouncementsManager = lazy(() => import("./pages/admin/marketing/AnnouncementsManager"));
const MarblismEmbed = lazy(() => import("./pages/admin/marketing/MarblismEmbed"));
const UTMLinkBuilder = lazy(() => import("./pages/admin/marketing/UTMLinkBuilder"));
const LiveSupportDashboard = lazy(() => import("./pages/admin/support/LiveSupportDashboard"));
const TeamChat = lazy(() => import("./pages/admin/team/TeamChat"));
const TimeTrackingDashboard = lazy(() => import("./pages/admin/payroll/TimeTrackingDashboard"));
const MentorAssignments = lazy(() => import("./pages/admin/training/MentorAssignments"));
const ProductEdit = lazy(() => import("./pages/admin/products/ProductEdit"));
const ProductList = lazy(() => import("./pages/admin/products/ProductList"));
const ProfilePage = lazy(() => import("./pages/admin/profile/ProfilePage"));
const SchedulingPage = lazy(() => import("./pages/admin/scheduling/SchedulingPage"));
const ScopeDataDocDetails = lazy(() => import("./pages/admin/systemstatus/ScopeDataDocDetails"));
const ScopeDataStudio = lazy(() => import("./pages/admin/systemstatus/ScopeDataStudio"));
const EbookDetails = lazy(() => import("./pages/admin/systemstatus/EbookDetails"));
const StripeTester = lazy(() => import("./pages/admin/systemstatus/StripeTester"));
const SubcollectionWriter = lazy(() => import("./pages/admin/systemstatus/SubcollectionWriter"));
const SystemOverviewDashboard = lazy(() => import("./pages/admin/systemstatus/SystemOverviewDashboard"));
const AdminChecklistDashboard = lazy(() => import("./pages/admin/checklist/AdminChecklistDashboard"));
const AdminChecklistDetail = lazy(() => import("./pages/admin/checklist/AdminChecklistDetail"));
const ClaudeChat = lazy(() => import("./pages/admin/tools/ClaudeChat"));
const DormantLinks = lazy(() => import("./pages/admin/tools/DormantLinks"));
const EmployeePolicyPage = lazy(() => import("./pages/admin/policies/EmployeePolicyPage"));

/* SPECIALIST / CLIENT SUCCESS */
const SpecialistDashboard = lazy(() => import("./pages/specialist/SpecialistDashboard"));
const SupportQueue = lazy(() => import("./pages/specialist/SupportQueue"));
const CourseLinkChecker = lazy(() => import("./pages/specialist/CourseLinkChecker"));
const StudioPreview = lazy(() => import("./pages/admin/studio/StudioPreview"));
const UserActivityDashboard = lazy(() => import("./pages/specialist/UserActivityDashboard"));
const SpecialistHelpPage = lazy(() => import("./pages/specialist/SpecialistHelpPage"));
const ContentTaskQueue = lazy(() => import("./pages/specialist/ContentTaskQueue"));

/* HELP PAGES */
const AdminHelpPage = lazy(() => import("./pages/admin/AdminHelpPage"));
const ClientHelpPage = lazy(() => import("./pages/client/ClientHelpPage"));
const MentorHelpPage = lazy(() => import("./pages/mentor/MentorHelpPage"));

/* CLIENT / LEARNER */
const AdaptiveExam = lazy(() => import("./pages/assessments/AdaptiveExam"));
const AIExamResults = lazy(() => import("./pages/assessments/AIExamResults"));
const AssessmentComparison = lazy(() => import("./pages/assessments/AssessmentComparison"));
const AssessmentAnalysis = lazy(() => import("./pages/assessments/AssessmentAnalysis"));
// Skills Diagnostic v2 + PMP Readiness Course (Nov 2026)
const DiagnosticHome = lazy(() => import("./pages/diagnostic/DiagnosticHome"));
const DiagnosticPlayer = lazy(() => import("./pages/diagnostic/DiagnosticPlayer"));
const DiagnosticResults = lazy(() => import("./pages/diagnostic/DiagnosticResults"));
const ReadinessCourse = lazy(() => import("./pages/diagnostic/ReadinessCourse"));
const ReadinessLesson = lazy(() => import("./pages/diagnostic/ReadinessLesson"));
const LegacyAssessmentRoute = lazy(() => import("./pages/diagnostic/LegacyAssessmentRoute"));

/* GENERATED COURSES */
const MyGeneratedCourses = lazy(() => import("./pages/client/courses/MyGeneratedCourses"));
const GeneratedCourseViewer = lazy(() => import("./pages/client/courses/GeneratedCourseViewer"));
const ClientDashboard = lazy(() => import("./pages/client/ClientDashboard"));
const ClientDownloads = lazy(() => import("./pages/client/ClientDownloads"));
const EbookLibrary = lazy(() => import("./pages/client/EbookLibrary"));
const ClientMyCourses = lazy(() => import("./pages/client/ClientMyCourses"));
const ClientReader = lazy(() => import("./pages/client/ClientReader"));
const IntroCpmai = lazy(() => import("./pages/learn/pmi-cpmai/CpmaiIntro.jsx"));
const CpmaiModulePlayer = lazy(() => import("./pages/learn/pmi-cpmai/CpmaiModulePlayer.jsx"));
const LearnCoursePage = lazy(() => import("./pages/learn/LearnCoursePage"));
const PMPFoundationsPlayer = lazy(() => import("./pages/learner/pmp/PMPFoundationsPlayer"));
const LearningExperienceDetail = lazy(() => import("./pages/client/LearningExperienceDetail"));
const MyIntakeForm = lazy(() => import("./pages/client/MyIntakeForm"));
const MyExams = lazy(() => import("./pages/client/MyExams"));
const MyLearningTools = lazy(() => import("./pages/client/MyLearningTools"));
const MyProfile = lazy(() => import("./pages/client/MyProfile"));
const NotificationSettings = lazy(() => import("./pwa/NotificationSettings"));
const PMSimulator = lazy(() => import("./pages/learn/simulator/PMSimulator"));
// PMO Dashboard Simulator retired Nov 2026: /learn/simulator/pmo-dashboard redirects to the PM Simulator
const PmpPracticeExamVolume1 = lazy(() => import("./pages/learn/PracExams/PmpPracticeExamVolume1.jsx"));
const PmpScenarioIntelligenceExam = lazy(() => import("./pages/learn/PracExams/PmpScenarioIntelligenceExam"));
const PmpScenarioResults = lazy(() => import("./pages/learn/PracExams/PmpScenarioResults"));
const PmpScenarioReview = lazy(() => import("./pages/learn/PracExams/PmpScenarioReview"));
const ResumeCheckout = lazy(() => import("./pages/client/ResumeCheckout"));
const ScopeAIPage = lazy(() => import("./pages/client/ScopeAIPage.jsx"));
const LearnerCourseProgress = React.lazy(() => import("./pages/learner/LearnerCourseProgress"));
const PMCoach = lazy(() => import("./pages/learner/PMCoach"));
const FullCoursePlayer = lazy(() => import("./pages/learner/FullCoursePlayer"));
const RemediationPlayer = lazy(() => import("./pages/learner/RemediationPlayer"));
const DeliverablesSuite = lazy(() => import("./pages/learner/DeliverablesSuite"));
const BadgeCongratulations = lazy(() => import("./pages/learner/BadgeCongratulations"));

/* LEARNER CLASSROOMS */
const MyClassroom = lazy(() => import("./pages/learner/classroom/MyClassroom"));
const WeekView = lazy(() => import("./pages/learner/classroom/WeekView"));
const ClassroomQuiz = lazy(() => import("./pages/learner/classroom/ClassroomQuiz"));
const AssignmentSubmission = lazy(() => import("./pages/learner/classroom/AssignmentSubmission"));
const MyGrades = lazy(() => import("./pages/learner/classroom/MyGrades"));
const FinalExam = lazy(() => import("./pages/learner/classroom/FinalExam"));
const SelfPacedPlayer = lazy(() => import("./pages/learner/classroom/SelfPacedPlayer"));
const OnlineSubmission = lazy(() => import("./pages/learner/classroom/OnlineSubmission"));
const CourseWelcome = lazy(() => import("./pages/learner/classroom/CourseWelcome"));
const MyProgress = lazy(() => import("./pages/client/LearnerStats"));

/* PMBOK 8 CONTACT HOURS (July 2026 ECO) */
const Pmbok8CourseBlueprint = lazy(() => import("./pages/public/Pmbok8CourseBlueprint"));
const PMBOK8CourseDashboard = lazy(() => import("./pages/learner/pmbok8/PMBOK8CourseDashboard"));
const PMBOK8DomainPage = lazy(() => import("./pages/learner/pmbok8/PMBOK8DomainPage"));
const PMBOK8CoursePlayer = lazy(() => import("./pages/learner/pmbok8/PMBOK8CoursePlayer"));
const PMBOK8FinalExam = lazy(() => import("./pages/learner/pmbok8/PMBOK8FinalExam"));
const PMBOK8Certificate = lazy(() => import("./components/pmbok8/PMBOK8Certificate"));
const DeliverablesSuiteLanding = lazy(() => import("./pages/public/DeliverablesSuiteLanding"));
const ProjectManagementTraining = lazy(() => import("./pages/public/ProjectManagementTraining"));
const EbooksStore = lazy(() => import("./pages/public/EbooksStore"));
const EbookSuccess = lazy(() => import("./pages/public/EbookSuccess"));

/* 30-DAY CHALLENGE: retired Nov 2026; its route redirects to Daily Practice */

/* AUDIO TEST (temporary) */

/* AUDIO QA DASHBOARD */

/* PROMPT ENGINEERING MASTERY */
const PromptEngineeringLanding = lazy(() => import("./pages/public/PromptEngineeringLanding"));
const PromptCourseDashboard = lazy(() => import("./pages/learner/prompt-engineering/PromptCourseDashboard"));
const PromptCoursePlayer = lazy(() => import("./pages/learner/prompt-engineering/PromptCoursePlayer"));
const PromptCertificate = lazy(() => import("./components/prompt/PromptCertificate"));

/* AI SYSTEMS DELIVERY */
const AISystemsDeliveryLanding = lazy(() => import("./pages/public/AISystemsDeliveryLanding"));
const AIDeliveryCourseDashboard = lazy(() => import("./pages/learner/ai-delivery/AIDeliveryCourseDashboard"));
const AIDeliveryCoursePlayer = lazy(() => import("./pages/learner/ai-delivery/AIDeliveryCoursePlayer"));
const AIDeliveryCertificate = lazy(() => import("./components/ai-delivery/AIDeliveryCertificate"));

// UNIQUE COURSE PLAYER (The actual learning layout)
const PM101Player = lazy(() => import("./pages/courses/project-management-101/PM101Player"));
const MiniLessonPlayer = lazy(() => import("./pages/learn/MiniLessonPlayer"));
const AICourseOverviewPage = lazy(() => import("./pages/learn/ai-for-pms/AICourseOverviewPage"));
const AIFoundationsIntroduction = lazy(() => import("./pages/learn/ai-for-pms/AIFoundationsIntroduction"));
const AIPMModulePlayer = lazy(() => import("./pages/learn/ai-for-pms/AIPMModulePlayer"));
const AIPMCourseCertificate = lazy(() => import("./pages/learn/ai-for-pms/AIPMCourseCertificate"));
const DevCoursePlayer = lazy(() => import("./pages/learn/dev/DevCoursePlayer"));
const StakeholderMiniCourse = lazy(() => import("./pages/learn/StakeholderMiniCourse"));
const AgileVsHybridCourse = lazy(() => import("./pages/learn/AgileVsHybridCourse"));
// GAMES
const PMPDomainMatcher = lazy(() => import("./pages/learn/games/PMPDomainMatcher"));

// LEARNING TOOLS
const RiskAssessmentCalculator = lazy(() => import("./pages/learn/tools/RiskAssessmentCalculator"));
const AIRiskSnapshot = lazy(() => import("./pages/learn/tools/AIRiskSnapshot"));

// AI CLIENT DASHBOARD — Premium/Enterprise users get the AI dashboard, Free users get basic
const DashboardRouter = lazy(() => import("./pages/client/DashboardRouter"));
const OnboardingFlow = lazy(() => import("./pages/onboarding/OnboardingFlow"));

// INTERACTIVE TOOLS
const InteractiveToolsDashboard = lazy(() => import("./pages/client/InteractiveToolsDashboard"));
const FlashcardDashboard = lazy(() => import("./pages/client/flashcards/FlashcardDashboard"));
const FlashcardStudySession = lazy(() => import("./pages/client/flashcards/FlashcardStudySession"));
const FlashcardResults = lazy(() => import("./pages/client/flashcards/FlashcardResults"));
const ConceptMapperDashboard = lazy(() => import("./pages/client/conceptmapper/ConceptMapperDashboard"));
const ConceptMapViewer = lazy(() => import("./pages/client/conceptmapper/ConceptMapViewer"));
const QuickQuizDashboard = lazy(() => import("./pages/client/quickquiz/QuickQuizDashboard"));
const DailyPractice = lazy(() => import("./pages/client/practice/DailyPractice"));
const ThirtyDayChallenge = lazy(() => import("./pages/learner/skill-builders/ThirtyDayChallenge"));
const QuickQuizPlayer = lazy(() => import("./pages/client/quickquiz/QuickQuizPlayer"));
const PracticeScenariosDashboard = lazy(() => import("./pages/client/scenarios/PracticeScenariosDashboard"));
const PracticeScenarioPlayer = lazy(() => import("./pages/client/scenarios/PracticeScenarioPlayer"));
const FillInTheBlankPlayer = lazy(() => import("./pages/client/fillintheblank/FillInTheBlankPlayer"));


/* --- MARKETING LANDING PAGE --- */
const ProjectManagement101 = lazy(() => import("./pages/courses/project-management-101/project-management-101"));

/* --- ADMIN-ONLY COURSES --- */
const MLPMCoursePlayer = lazy(() => import("./pages/learn/mlpm/MLPMCoursePlayer"));

/* ==================== 3. APP COMPONENT ==================== */

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white text-slate-700 dark:bg-slate-950 dark:text-slate-200">
      Loading...
    </div>
  );
}

/** Tracks page visits + last-clicked elements for 404 error context */
function NavigationTracker() {
  const location = useLocation();
  useEffect(() => {
    recordPageVisit(location.pathname + location.search);
  }, [location.pathname, location.search]);
  useEffect(() => {
    document.addEventListener("click", handleGlobalClick, true);
    return () => document.removeEventListener("click", handleGlobalClick, true);
  }, []);

  // Global listener for Firestore permission-denied errors
  useEffect(() => {
    const handlePermissionError = (event) => {
      const err = event.reason;
      if (!err) return;
      const msg = err?.message || "";
      const code = err?.code || "";
      if (
        code === "permission-denied" ||
        code === "functions/permission-denied" ||
        /missing or insufficient permissions/i.test(msg)
      ) {
        const currentUser = auth.currentUser;
        logPermissionDenied({
          uid: currentUser?.uid,
          email: currentUser?.email,
          userName: currentUser?.displayName || currentUser?.email,
          page: window.location.pathname + window.location.search,
          error: err,
        });
      }
    };
    window.addEventListener("unhandledrejection", handlePermissionError);
    return () => window.removeEventListener("unhandledrejection", handlePermissionError);
  }, []);

  return null;
}

/** Scroll to top on every route change */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/** Redirect legacy /courses/learn/:courseId to /learn/:courseId */
function CoursesLearnRedirect() {
  const { courseId } = useParams();
  return <Navigate to={`/learn/${courseId}`} replace />;
}

/** Top-of-page banner for FCM foreground messages (replaces browser OS popups) */
function FCMBanner() {
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setBanner(e.detail);
      // Auto-dismiss after 7 seconds
      setTimeout(() => setBanner(null), 7000);
    };
    window.addEventListener("fcm-banner", handler);
    return () => window.removeEventListener("fcm-banner", handler);
  }, []);

  if (!banner) return null;

  return (
    <div
      style={{ zIndex: 999998 }}
      className="fixed top-0 left-0 right-0 flex items-center justify-between gap-3 px-5 py-3 bg-indigo-700 text-white text-sm font-semibold shadow-lg"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="shrink-0 text-indigo-200">🔔</span>
        <span className="font-bold truncate">{banner.title}</span>
        {banner.body && <span className="text-indigo-200 truncate hidden sm:block">— {banner.body}</span>}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {banner.link && (
          <a href={banner.link} className="underline text-indigo-100 hover:text-white text-xs">View</a>
        )}
        <button onClick={() => setBanner(null)} className="text-indigo-200 hover:text-white text-lg leading-none">&times;</button>
      </div>
    </div>
  );
}

// Sends an old in-site address to an external site (used for the retired About page).
function ExternalRedirect({ to }) {
  React.useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

export default function App() {
  // Initialize PWA-specific styles (adds body classes for standalone/mobile)
  // Inlined here to avoid importing the full pwa barrel which pulls in Firebase messaging
  useEffect(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isPWA) document.body.classList.add('pwa-standalone');
    if (window.matchMedia('(max-width: 768px)').matches) document.body.classList.add('pwa-mobile');
    window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
      document.body.classList.toggle('pwa-standalone', e.matches);
    });
    window.matchMedia('(max-width: 768px)').addEventListener('change', (e) => {
      document.body.classList.toggle('pwa-mobile', e.matches);
    });
  }, []);

  // Signal the NotificationBell when a new service worker version is waiting
  useEffect(() => {
    const handler = () => {
      window.__swUpdateAvailable = true;
      window.dispatchEvent(new CustomEvent("sw-update-state-change", { detail: { available: true } }));
    };
    window.addEventListener("sw-update-available", handler);
    return () => window.removeEventListener("sw-update-available", handler);
  }, []);

  return (
    <ErrorBoundary>
    <Toaster
      position="top-right"
      containerStyle={{ zIndex: 999999, top: 72 }}
      toastOptions={{
        duration: 4000,
        style: {
          maxWidth: 420,
          fontSize: "0.875rem",
          fontWeight: 600,
        },
        error: { duration: 6000 },
      }}
    />
    <Router>
      <FCMBanner />
      <NavigationTracker />
      <VisitorTracker />
      <PageTransition />
      <HavloController />
      <CourseReviewFeedback />
      <LiveChatWidget />
      <HumanRequestAlert />
      <ScrollToTop />
      {/* PWA Banners - lazy-loaded, fallback to null so they don't block render */}
      <Suspense fallback={null}>
        <OfflineBanner />
        <InstallBanner />
      </Suspense>

      <IdleSignout auth={auth} onSignoutPath="/">
        <AdminSubdomainGuard>
        <Suspense fallback={<PageLoader />}>
          <Routes>

            {/* ==================== PUBLIC ROUTES (HomePageLayout) ==================== */}
            <Route element={<HomePageLayout />}>
              <Route path="/" element={isAdminSubdomain() ? <Navigate to="/admin" replace /> : isEbooksSubdomain() ? <Navigate to="/ebooks" replace /> : <HomePage />} />
              <Route path="/aboutme" element={<AboutMe />} />
              <Route path="/about-t-laketia-jones" element={<ExternalRedirect to="https://www.tlaketia.com" />} />
              <Route path="/instructors" element={<MeetYourInstructors />} />
              <Route path="/ai-project-management" element={<Navigate to="/articles/ai-transforming-project-management" replace />} />
              <Route path="/articles" element={<ArticlesPage />} />
              <Route path="/articles/ai-transforming-project-management" element={<AITransformingPM />} />
              <Route path="/articles/teaching-ai-skills-project-managers" element={<TeachingAISkillsToPMs />} />
              <Route path="/articles/future-project-leadership-ai" element={<FutureOfProjectLeadership />} />
              <Route path="/articles/top-ai-tools-project-managers-2026" element={<TopAIToolsForPMs />} />
              <Route path="/articles/why-pms-need-ai-skills" element={<WhyPMsMustDevelopAISkills />} />
              <Route path="/articles/ai-risk-management-project-delivery" element={<AIRiskManagementForPMs />} />
              <Route path="/articles/ai-agile-project-delivery" element={<AIAgileProjectDelivery />} />
              <Route path="/articles/pmbok8-exam-changes-2026" element={<PMBOK8ExamChanges />} />
              <Route path="/articles/ai-automation-project-managers" element={<AIAutomationForProjectManagers />} />
              <Route path="/articles/ai-stakeholder-management" element={<AIStakeholderManagement />} />
              <Route path="/articles/building-ai-ready-pmo" element={<BuildingAIReadyPMO />} />
              <Route path="/articles/ai-data-driven-decisions-pm" element={<AIDataDrivenDecisionsPM />} />
              <Route path="/articles/ai-change-management-transformation" element={<AIChangeManagement />} />
              <Route path="/articles/ai-project-budgeting-cost-management" element={<AIProjectBudgeting />} />
              <Route path="/articles/ai-quality-management-continuous-improvement" element={<AIQualityManagement />} />
              <Route path="/articles/ai-resource-management-team-optimization" element={<AIResourceManagement />} />
              <Route path="/articles/best-ai-project-document-generator" element={<DeliverablesSuiteAIDocumentGenerator />} />
              <Route path="/articles/thescope180-project-dashboard-features" element={<TheScope180ProjectDashboard />} />
              <Route path="/articles/thescope180-academy-online-pm-courses" element={<TheScope180AcademyOnlinePMCourses />} />
              <Route path="/articles/live-pmp-study-groups-exam-prep" element={<LivePMPStudyGroupsExamPrep />} />
              <Route path="/speaking" element={<SpeakingPage />} />
              <Route path="/auth" element={<AuthAction />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/pmbok-7-scenario-based-questions" element={<Navigate to="/blog/pmbok-8-scenario-based-questions" replace />} />
              <Route path="/blog/:slug" element={<BlogPostDetail />} />
              <Route path="/certificate-preview" element={<CertificatePreviewRedirect />} />
              <Route path="/verify" element={<CertificateVerify />} />
              <Route path="/verify/:certId" element={<CertificateVerify />} />
              <Route path="/checkout/:productId" element={<CheckoutGate />} />
              <Route path="/checkout/:productId/pay" element={<PayForProduct />} />
              <Route path="/checkout/success" element={<PurchaseSuccess />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/socials" element={<SocialsPage />} />
              <Route path="/courses" element={<Navigate to="/academy" replace />} />
              <Route path="/courses/project-management-101" element={<Navigate to="/academy/project-management-101" replace />} />
              <Route path="/courses/:slug" element={<Navigate to="/academy" replace />} />
              <Route path="/skill-builders" element={<SkillBuildersPage />} />
              <Route path="/deliverables" element={<DeliverablesSuiteLanding />} />
              <Route path="/email-confirmed" element={<EmailConfirmedPage />} />
              <Route path="/academy/project-management-101" element={<ProjectManagement101 />} />
              {/* PMBOK 7 fully retired - all PMBOK 7 routes redirect to the PMBOK 8 course */}
              <Route path="/academy/pmp-exam-prep" element={<Navigate to="/academy/pmbok8-exam-prep" replace />} />
              <Route path="/pmbok7" element={<Navigate to="/academy/pmbok8-exam-prep" replace />} />
              <Route path="/academy/pmbok8-exam-prep" element={<Pmbok8CourseBlueprint />} />
              {/* Old marketing landing kept for reference; superseded by the blueprint page */}
              <Route path="/academy/pmbok8-blueprint" element={<Navigate to="/academy/pmbok8-exam-prep" replace />} />
              <Route path="/academy/prompt-engineering" element={<PromptEngineeringLanding />} />
              <Route path="/academy/ai-systems-delivery" element={<AISystemsDeliveryLanding />} />
              <Route path="/academy/ai-product-leadership-sp" element={<AIProductLeadershipSP />} />
              <Route path="/academy/ai-product-leadership-il" element={<Navigate to="/academy/ai-product-leadership-sp" replace />} />
              <Route path="/academy/ai-for-pms" element={<AIForPMsLanding />} />
              <Route path="/academy/ai-for-pms/:slug" element={<AIPMCourseDetail />} />
              <Route path="/academy" element={<LiveCourseCatalog />} />
              {/* Legacy public route redirects */}
              <Route path="/scope-academy" element={<Navigate to="/academy" replace />} />
              <Route path="/live-courses" element={<Navigate to="/academy" replace />} />
              <Route path="/ai-for-project-managers" element={<Navigate to="/academy/ai-for-pms" replace />} />
              <Route path="/ai-for-project-managers/:slug" element={<RedirectOldAIPMDetail />} />
              <Route path="/pmp-exam-prep" element={<Navigate to="/academy/pmbok8-exam-prep" replace />} />
              <Route path="/prompt-engineering" element={<Navigate to="/academy/prompt-engineering" replace />} />
              <Route path="/ai-systems-delivery" element={<Navigate to="/academy/ai-systems-delivery" replace />} />
              <Route path="/ai-product-leadership-sp" element={<Navigate to="/academy/ai-product-leadership-sp" replace />} />
              <Route path="/ai-product-leadership-il" element={<Navigate to="/academy/ai-product-leadership-sp" replace />} />
              {/* Live sessions retired - not currently hosting live training. Routes redirect to the Academy. */}
              <Route path="/livesessions" element={<Navigate to="/academy" replace />} />
              <Route path="/livesessions/pmp-study-group" element={<Navigate to="/academy" replace />} />
              <Route path="/livesessions/pmp-study-group/success" element={<Navigate to="/academy" replace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/studio/:lessonId" element={<StudioPreview />} />
              <Route path="/sso/:companySlug" element={<EnterpriseSSOLogin />} />
              <Route path="/pathfinder/summary" element={<PathfinderSummary />} />
              <Route path="/pm-certification-pathfinder" element={<PMCertificationPathfinder />} />
              <Route path="/pm-quiz" element={<PMQuiz />} />
              <Route path="/portfolio" element={<WorkSamples />} />
              <Route path="/practice-exams" element={<Navigate to="/products?tab=exams" replace />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:slug" element={<ProductDetailPage />} />
              <Route path="/recommended-training" element={<RecommendedTraining />} />
              <Route path="/project-management-training" element={<ProjectManagementTraining />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/samples/:sampleId" element={<SampleViewer />} />
              <Route path="/samples/oh-st-pm-toolkit" element={<OhSTPMToolkit />} />
              <Route path="/simulator/cpmai" element={<Navigate to="/products" replace />} />
              <Route path="/simulator/cpmai/next-step" element={<Navigate to="/products" replace />} />
              <Route path="/simulator/pmbok7" element={<Navigate to="/academy/pmbok8-exam-prep" replace />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/waitlist" element={<Waitlist />} />
              <Route path="/waitlist/:sessionId" element={<Waitlist />} />
              <Route path="/waitlist/login" element={<WaitlistLogin />} />
              <Route path="/waitlist/thanks" element={<WaitlistThanks />} />
              <Route path="/tester/sign-nda" element={<Navigate to="/" replace />} />
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />

              {/* Old or mistyped addresses still used by older emails, the installed app and
                  bookmarks. Each one goes straight to the current page (Nov 2026 link audit). */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/learner" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/dashboard" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/client" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/client/dashboard" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/client/my-courses" element={<Navigate to="/learner/my-courses" replace />} />
              <Route path="/client/my-generated-courses" element={<Navigate to="/learner/my-generated-courses" replace />} />
              <Route path="/client/*" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/director/dashboard" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/developer/dashboard" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/support/dashboard" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/manager/dashboard" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/associate/dashboard" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/account" element={<Navigate to="/learner/profile" replace />} />
              <Route path="/account/billing" element={<Navigate to="/subscription/manage" replace />} />
              <Route path="/account/referrals" element={<Navigate to="/learner/profile" replace />} />
              <Route path="/subscription" element={<Navigate to="/subscribe" replace />} />
              <Route path="/preferences" element={<Navigate to="/settings/notifications" replace />} />
              <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
              <Route path="/about" element={<Navigate to="/aboutme" replace />} />
              <Route path="/services" element={<Navigate to="/products" replace />} />
              <Route path="/services/*" element={<Navigate to="/products" replace />} />
              <Route path="/program" element={<Navigate to="/academy/ai-for-pms" replace />} />
              <Route path="/community" element={<Navigate to="/socials" replace />} />
              <Route path="/live-sessions" element={<Navigate to="/academy" replace />} />
              <Route path="/live-sessions/*" element={<Navigate to="/academy" replace />} />
              <Route path="/marketplace/*" element={<Navigate to="/ebooks" replace />} />
              <Route path="/learner/academy" element={<Navigate to="/academy/my" replace />} />
              <Route path="/learner/video-studio" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/admin/support" element={<Navigate to="/admin/live-support" replace />} />
              <Route path="/admin/live-sessions" element={<Navigate to="/admin/live-support" replace />} />
              <Route path="/admin/system-status" element={<Navigate to="/admin/system/overview" replace />} />

              {/* Course building is retired: Claude builds the courses, so the course creation,
                  review/approval, Content Manager, Video Studio, classroom and instructor tools are
                  gone. Their old addresses go to each person's dashboard (Nov 2026). */}
              <Route path="/admin/content/*" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/generated-courses" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/users/:userId/generated-course/:courseId" element={<Navigate to="/admin/clients" replace />} />
              <Route path="/admin/classrooms/*" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/livesessions" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/training/courses/*" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/training/cpmai-verify" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/simulation/*" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/audio-qa" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/content-manager/*" element={<Navigate to="/specialist/dashboard" replace />} />
              <Route path="/specialist/content/*" element={<Navigate to="/specialist/dashboard" replace />} />
              <Route path="/specialist/content-upload" element={<Navigate to="/specialist/dashboard" replace />} />
              <Route path="/specialist/video-studio" element={<Navigate to="/specialist/dashboard" replace />} />
              <Route path="/specialist/heygen" element={<Navigate to="/specialist/dashboard" replace />} />
              <Route path="/specialist/video-status" element={<Navigate to="/specialist/dashboard" replace />} />
              <Route path="/specialist/learning-tools" element={<Navigate to="/specialist/dashboard" replace />} />
              <Route path="/instructor" element={<Navigate to="/learner/dashboard" replace />} />
              <Route path="/instructor/*" element={<Navigate to="/learner/dashboard" replace />} />
            </Route>

            <Route path="/newsletter" element={<NewsletterSignup />} />
            <Route path="/redirect" element={<PostLoginRedirect />} />

            {/* Standalone eBook store (also served at ebooks.thescope180.com) */}
            <Route path="/ebooks" element={<EbooksStore />} />
            <Route path="/ebooks/success" element={<EbookSuccess />} />

            {/* ==================== SPECIALIST / CLIENT SUCCESS ROUTES ==================== */}
            <Route element={<RequireRoles allow={["specialist", "admin", "content-manager"]} />}>
              <Route element={<SpecialistLayout />}>
                <Route path="/specialist" element={<SpecialistDashboard />} />
                <Route path="/client-success" element={<SpecialistDashboard />} />
                <Route path="/specialist/dashboard" element={<SpecialistDashboard />} />
                <Route path="/specialist/support-queue" element={<SupportQueue />} />
                <Route path="/specialist/link-checker" element={<CourseLinkChecker />} />
                <Route path="/specialist/user-activity" element={<UserActivityDashboard />} />
                <Route path="/specialist/help" element={<SpecialistHelpPage />} />
                <Route path="/specialist/live-support" element={<LiveSupportDashboard />} />
                <Route path="/specialist/time-tracking" element={<TimeTrackingDashboard />} />
                <Route path="/specialist/content-tasks" element={<ContentTaskQueue />} />
                <Route path="/specialist/team-chat" element={<TeamChat />} />
                <Route path="/specialist/employee-policy" element={<EmployeePolicyPage />} />

              </Route>
            </Route>

            {/* ==================== STANDALONE PRODUCT: PROJECT DASHBOARD ==================== */}
            <Route path="/projects" element={<ProjectDashboardLayout />}>
              <Route index element={<ProjectDashboardLanding />} />
              <Route path="login" element={<ProjectDashboardAuth />} />
              <Route path="register" element={<ProjectDashboardAuth />} />
              <Route path="portfolio" element={<ProjectPortfolio />} />
              <Route path="portfolio/:projectId" element={<ProjectDashboardApp />} />
              <Route path="welcome" element={<ProjectPortfolio />} />
              <Route path="welcome/:projectId" element={<ProjectDashboardApp />} />
              <Route path="shared/:shareToken" element={<SharedProjectView />} />
            </Route>

            {/* ==================== PROTECTED DASHBOARD ROUTES (DashboardLayout) ==================== */}
            <Route element={<DashboardLayout />}>
              
              {/* ---------- ADMIN ---------- */}
              <Route element={<RequireRoles allow={["admin"]} />}>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/blog-manager" element={<Navigate to="/admin/marketing/blog" replace />} />
                <Route path="/admin/clients" element={<AdminClientDashboard />} />
                <Route path="/admin/clients/:clientId" element={<ClientDetail />} />
                <Route path="/admin/email/emailsettings" element={<EmailSettings />} />
                <Route path="/admin/employees" element={<Employees />} />
                <Route path="/admin/exams/bulk-upload" element={<ExamBulkUploader />} />
                <Route path="/admin/finances" element={<FinancesPage />} />
                <Route path="/admin/site-statistics" element={<SiteStatistics />} />
                <Route path="/admin/learner-activity" element={<LearnerActivity />} />
                <Route path="/admin/pmbok8-time-log" element={<Pmbok8TimeLog />} />
                <Route path="/admin/youtube" element={<YouTubePublisher />} />
                <Route path="/admin/studio-intro/:moduleId" element={<StudioIntroPreview />} />
                <Route path="/admin/contracts" element={<ContractsDashboard />} />
                <Route path="/admin/partners" element={<PartnersPage />} />
                <Route path="/admin/partners/:partnerId" element={<PartnerDetailPage />} />
                <Route path="/admin/outreach" element={<OutreachPage />} />
                <Route path="/admin/opportunities" element={<OpportunitiesPage />} />
                <Route path="/admin/work-board" element={<WorkBoardPage />} />
                <Route path="/admin/sam-status" element={<SamStatusPage />} />
                <Route path="/admin/sam-scan" element={<SamScanPage />} />
                <Route path="/admin/inbox" element={<InboxPage />} />
                <Route path="/admin/ndas/:requestId" element={<NdaCountersignPage />} />
                <Route path="/admin/intake/add" element={<AddClientIntakeForm />} />
                <Route path="/admin/marketing" element={<MarketingHub />} />
                <Route path="/admin/marketing/overview" element={<MarketingDashboard />} />
                <Route path="/admin/marketing/email" element={<EmailMarketing />} />
                <Route path="/admin/marketing/emails" element={<MarketingEmails />} />
                <Route path="/admin/marketing/landing" element={<MarketingLanding />} />
                <Route path="/admin/marketing/leads" element={<MarketingLeads />} />
                <Route path="/admin/marketing/subscribers" element={<NewsletterSubscribers />} />
                <Route path="/admin/marketing/newsletter" element={<NewsletterHub />} />
                <Route path="/admin/marketing/newsletter/composer" element={<NewsletterComposer />} />
                <Route path="/admin/marketing/newsletter/automation" element={<NewsletterAutomation />} />
                <Route path="/admin/marketing/content-calendar" element={<ContentCalendar />} />
                <Route path="/admin/marketing/newsletter-studio" element={<NewsletterStudio />} />
                <Route path="/admin/marketing/newsletter-composer" element={<Navigate to="/admin/marketing/newsletter/composer" replace />} />
                <Route path="/admin/marketing/assets" element={<AssetsManager />} />
                <Route path="/admin/marketing/blog" element={<MarketingBlog />} />
                <Route path="/admin/marketing/seo" element={<SEOManager />} />
                <Route path="/admin/marketing/analytics" element={<AnalyticsDashboard />} />
                <Route path="/admin/marketing/social" element={<SocialMedia />} />
                <Route path="/admin/marketing/promotions" element={<Promotions />} />
                <Route path="/admin/marketing/announcements" element={<AnnouncementsManager />} />
                <Route path="/admin/marketing/marblism" element={<MarblismEmbed />} />
                <Route path="/admin/marketing/utm-links" element={<UTMLinkBuilder />} />
                <Route path="/admin/messages" element={<AdminInbox />} />
                <Route path="/admin/products" element={<ProductList />} />
                <Route path="/admin/products/new" element={<ProductEdit />} />
                <Route path="/admin/products/edit/:productId" element={<ProductEdit />} />
                <Route path="/admin/profile" element={<ProfilePage />} />
                <Route path="/admin/scheduling" element={<SchedulingPage />} />
                <Route path="/admin/ScopeDataStudio" element={<ScopeDataStudio />} />
                <Route path="/admin/ScopeDataStudio/:collectionId/:docId" element={<ScopeDataDocDetails />} />
                <Route path="/admin/ebooks/:id" element={<EbookDetails />} />
                <Route path="/admin/system/overview" element={<SystemOverviewDashboard />} />
                <Route path="/admin/system/stripe-tester" element={<StripeTester />} />
                <Route path="/admin/systemstatus/SubcollectionWriter/:collectionId/:docId/:subcollection" element={<SubcollectionWriter />} />


                <Route path="/admin/training/mentors" element={<MentorAssignments />} />


                <Route path="/admin/checklist" element={<AdminChecklistDashboard />} />
                <Route path="/admin/checklist/:id" element={<AdminChecklistDetail />} />
                <Route path="/admin/live-support" element={<LiveSupportDashboard />} />
                <Route path="/admin/team-chat" element={<TeamChat />} />
                <Route path="/admin/payroll" element={<TimeTrackingDashboard />} />
                <Route path="/admin/time-tracking" element={<Navigate to="/admin/payroll" replace />} />
                <Route path="/admin/users" element={<UserManagement />} />
                <Route path="/admin/help" element={<AdminHelpPage />} />
                <Route path="/admin/tools/dormant-links" element={<DormantLinks />} />
                <Route path="/admin/tools/claude-chat" element={<ClaudeChat />} />
                <Route path="/admin/tools/pm-coach" element={<PMCoach />} />
                <Route path="/admin/employee-policy" element={<Navigate to="/specialist/employee-policy" replace />} />
                <Route path="/admin/tester-management" element={<Navigate to="/admin/dashboard" replace />} />



                {/* ML-PM 90-DAY COURSE (admin-only) */}
                <Route path="/admin/ml-pm-90" element={<MLPMCoursePlayer />} />

                </Route>


            {/* ---------- MENTOR ---------- */}
            <Route element={<RequireRoles allow={["mentor", "admin"]} />}>
              <Route path="/mentor" element={<ClientDashboard />} />
              <Route path="/mentor/dashboard" element={<ClientDashboard />} />
              <Route path="/mentor/help" element={<MentorHelpPage />} />
            </Route>

           {/* ---------- CLIENT / LEARNER ---------- */}
           <Route element={<RequireRoles allow={["client", "learner", "admin", "specialist", "mentor", "director", "associate", "content-manager", "instructor"]} />}>
                {/* Skills Diagnostic v2 + PMP Readiness Course */}
                <Route path="/diagnostic" element={<DiagnosticHome />} />
                <Route path="/diagnostic/:attemptId" element={<DiagnosticPlayer />} />
                <Route path="/diagnostic/:attemptId/results" element={<DiagnosticResults />} />
                <Route path="/learner/readiness-course" element={<ReadinessCourse />} />
                <Route path="/learner/readiness-course/:taskKey" element={<ReadinessLesson />} />
                {/* Old diagnostic screens: v2 attempts go to the new pages; older 30-question
                    attempts keep their player and analysis. The $79/$100/"normally $197" course
                    offers and the AI coach-note Study Plan are retired (Nov 2026). */}
                <Route path="/assessments/:assessmentId" element={<LegacyAssessmentRoute view="player" fallback={<AdaptiveExam />} />} />
                <Route path="/assessments/:assessmentId/analysis" element={<LegacyAssessmentRoute view="results" fallback={<AssessmentAnalysis />} />} />
                <Route path="/assessments/start" element={<Navigate to="/diagnostic" replace />} />
                <Route path="/assessments/:attemptId/comparison" element={<AssessmentComparison />} />
                <Route path="/assessments/:attemptID/course-preview" element={<Navigate to="/learner/readiness-course" replace />} />
                <Route path="/assessments/course-purchase/:attemptID" element={<Navigate to="/learner/readiness-course" replace />} />
                <Route path="/assessments/:attemptId/plan" element={<Navigate to="/learner/readiness-course" replace />} />
                <Route path="/learner/study-plan" element={<Navigate to="/learner/readiness-course" replace />} />
                <Route path="/onboarding" element={<OnboardingFlow />} />
                <Route path="/learner/dashboard" element={<DashboardRouter />} />
                <Route path="/learner/ai-dashboard" element={<Navigate to="/learner/dashboard" replace />} />
                <Route path="/learner/downloads" element={<ClientDownloads />} />
                <Route path="/learner/ebooks" element={<EbookLibrary />} />
                <Route path="/learner/exam-results/:attemptID" element={<AIExamResults />} />
                <Route path="/learner/learning-experiences/:id" element={<LearningExperienceDetail />} />
                {/* Live sessions retired - not currently hosting live training */}
                <Route path="/learner/livesessions" element={<Navigate to="/learner/dashboard" replace />} />

                {/* LIVE CLASSROOMS */}
                <Route path="/learner/classrooms" element={<MyClassroom />} />
                <Route path="/learner/classrooms/ai-for-pms" element={<Navigate to="/academy/my/ai-for-pms" replace />} />
                <Route path="/learner/classrooms/ai-for-project-managers" element={<Navigate to="/academy/my/ai-for-pms" replace />} />
                <Route path="/learner/classrooms/:id" element={<MyClassroom />} />
                <Route path="/learner/classrooms/:id/week/:week" element={<WeekView />} />
                <Route path="/learner/classrooms/:id/week/:week/quiz" element={<ClassroomQuiz />} />
                <Route path="/learner/classrooms/:id/week/:week/assignment" element={<AssignmentSubmission />} />
                <Route path="/learner/classrooms/:id/grades" element={<MyGrades />} />
                <Route path="/learner/classrooms/:id/final-exam" element={<FinalExam />} />
                <Route path="/learner/self-paced/:courseSlug" element={<SelfPacedPlayer />} />
                <Route path="/learner/self-paced/:courseSlug/submit/:assignmentId" element={<OnlineSubmission />} />
                <Route path="/learner/self-paced/:courseSlug/welcome" element={<CourseWelcome />} />
                {/* ACADEMY DASHBOARD */}
                <Route path="/academy/my" element={<AcademyDashboard />} />

                {/* Retired PMBOK 7-era 35-hour course: every address goes to the PMBOK 8 course */}
                <Route path="/academy/my/pmp-exam-prep" element={<Navigate to="/academy/my/pmbok8-exam-prep" replace />} />
                <Route path="/academy/my/pmp-exam-prep/final-exam" element={<Navigate to="/academy/my/pmbok8-exam-prep/final-exam" replace />} />
                <Route path="/academy/my/pmp-exam-prep/certificate" element={<Navigate to="/academy/my/pmbok8-exam-prep/certificate" replace />} />
                <Route path="/academy/my/pmp-exam-prep/*" element={<Navigate to="/academy/my/pmbok8-exam-prep" replace />} />

                {/* PMBOK 8 CONTACT HOURS (July 2026 ECO) */}
                <Route path="/academy/my/pmbok8-exam-prep" element={<PMBOK8CourseDashboard />} />
                <Route path="/academy/my/pmbok8-exam-prep/certificate" element={<PMBOK8Certificate />} />
                <Route path="/academy/my/pmbok8-exam-prep/final-exam" element={<PMBOK8FinalExam />} />
                <Route path="/academy/my/pmbok8-exam-prep/:domainKey/:moduleId" element={<PMBOK8CoursePlayer />} />
                <Route path="/academy/my/pmbok8-exam-prep/:domainKey" element={<PMBOK8DomainPage />} />

                {/* PROMPT ENGINEERING MASTERY */}
                <Route path="/academy/my/prompt-engineering" element={<PromptCourseDashboard />} />
                <Route path="/academy/my/prompt-engineering/certificate" element={<PromptCertificate />} />
                <Route path="/academy/my/prompt-engineering/:moduleId" element={<PromptCoursePlayer />} />

                {/* AI SYSTEMS DELIVERY */}
                <Route path="/academy/my/ai-systems-delivery" element={<AIDeliveryCourseDashboard />} />
                <Route path="/academy/my/ai-systems-delivery/certificate" element={<AIDeliveryCertificate />} />
                <Route path="/academy/my/ai-systems-delivery/:moduleId" element={<AIDeliveryCoursePlayer />} />

                <Route path="/learner/ai-courses" element={<Navigate to="/learner/my-courses" replace />} />
                <Route path="/learner/ai-courses/full/:courseId" element={<FullCoursePlayer />} />
                <Route path="/learner/ai-courses/remediation/:courseId" element={<RemediationPlayer />} />
                <Route path="/suite/deliverables" element={<DeliverablesSuite />} />
                <Route path="/suite/deliverables/:templateId" element={<DeliverablesSuite />} />
                <Route path="/learner/my-courses" element={<ClientMyCourses />} />
                <Route path="/learner/my-exams" element={<MyExams />} />
                <Route path="/learner/my-learning-tools" element={<MyLearningTools />} />
                <Route path="/learner/my-learning-experiences" element={<Navigate to="/learner/my-exams" replace />} />
                <Route path="/learner/profile" element={<MyProfile />} />
                <Route path="/subscription/manage" element={<ManageSubscription />} />
                <Route path="/learner/reader/:productId" element={<ClientReader />} />
                <Route path="/learner/my-generated-courses" element={<MyGeneratedCourses />} />
                <Route path="/learner/generated-course/:courseId" element={<GeneratedCourseViewer />} />
                <Route path="/courses/learn/stakeholder-management" element={<Navigate to="/learn/stakeholder-management" replace />} />
                <Route path="/courses/learn/:courseId" element={<CoursesLearnRedirect />} />
                <Route path="/learner/resume-checkout" element={<ResumeCheckout />} />
                <Route path="/learner/support" element={<ScopeAIPage />} />
                <Route path="/learner/help" element={<ClientHelpPage />} />
                <Route path="/learner/skill-builders" element={<InteractiveToolsDashboard />} />
                <Route path="/learner/skill-builders/30-day-challenge" element={<ThirtyDayChallenge />} />
                <Route path="/learner/interactive-tools" element={<Navigate to="/learner/skill-builders" replace />} />
                <Route path="/learner/practice" element={<DailyPractice />} />
                <Route path="/learner/flashcards" element={<FlashcardDashboard />} />
                <Route path="/learner/flashcards/:examType/study" element={<FlashcardStudySession />} />
                <Route path="/learner/flashcards/:examType/results" element={<FlashcardResults />} />
                <Route path="/learner/concept-mapper" element={<ConceptMapperDashboard />} />
                <Route path="/learner/concept-mapper/:mapId" element={<ConceptMapViewer />} />
                <Route path="/learner/quick-quiz" element={<QuickQuizDashboard />} />
                <Route path="/learner/quick-quiz/play" element={<QuickQuizPlayer />} />
                <Route path="/learner/practice-scenarios" element={<PracticeScenariosDashboard />} />
                <Route path="/learner/practice-scenarios/play" element={<PracticeScenarioPlayer />} />
                <Route path="/learner/fill-in-blank" element={<FillInTheBlankPlayer />} />
                <Route path="/dashboard/my-intake" element={<MyIntakeForm />} />
                
{/* GAMES */}
<Route path="/learn/games/pmp-domain-matcher" element={<PMPDomainMatcher />} />

{/* LEARNING TOOLS - Specific routes before generic :slug */}
<Route path="/learn/risk-assessment-calculator" element={<Suspense fallback={<div>Loading...</div>}><RiskAssessmentCalculator /></Suspense>} />
<Route path="/learn/ai-risk-snapshot" element={<Suspense fallback={<div>Loading...</div>}><AIRiskSnapshot /></Suspense>} />

{/* UNIQUE COURSE PLAYERS (Placed above generic) */}
<Route path="/academy/my/project-management-101" element={<PM101Player />} />
<Route path="/learn/pmi-cpmai/player" element={<IntroCpmai />} />
<Route path="/learn/pmi-cpmai-certification-prep/module/module-0" element={<IntroCpmai />} />
<Route path="/learn/pmi-cpmai-certification-prep/module/:moduleKey" element={<CpmaiModulePlayer />} />

{/* AI FOR PMS BUNDLE DASHBOARD */}
<Route path="/academy/my/ai-for-pms" element={<AIPMBundlePage />} />

{/* AI COURSE OVERVIEW PAGE - Shows video, objectives, module preview */}
<Route path="/academy/my/ai-for-pms/:slug/certificate" element={<AIPMCourseCertificate />} />
<Route path="/academy/my/ai-for-pms/:slug/introduction" element={<AIFoundationsIntroduction />} />
<Route path="/academy/my/ai-for-pms/:slug/module/:moduleIndex" element={<AIPMModulePlayer />} />
<Route path="/academy/my/ai-for-pms/:slug" element={<AICourseOverviewPage />} />

{/* AI Foundations now uses DevCoursePlayer via /learn/dev/ route */}

{/* STAKEHOLDER MINI COURSE - Free 3-tab player */}
<Route path="/learn/stakeholder-management/module/:moduleKey" element={<StakeholderMiniCourse />} />
<Route path="/learn/stakeholder-management" element={<StakeholderMiniCourse />} />

{/* AGILE VS HYBRID MINI COURSE - Paid / premium 4-tab player */}
<Route path="/learn/agile-vs-hybrid" element={<AgileVsHybridCourse />} />

{/* Oracle EPM courses are off the site - old addresses go to the Academy */}
<Route path="/learn/oracle/*" element={<Navigate to="/academy" replace />} />

{/* DEV COURSE PLAYER - Dynamic player for learningTools courses */}
{/* AI PM courses are redirected to /academy/my/ai-for-pms/ via DevPlayerGuard */}
<Route path="/learn/dev/:slug/module/:moduleKey" element={<DevPlayerGuard><DevCoursePlayer /></DevPlayerGuard>} />
<Route path="/learn/dev/:slug" element={<DevPlayerGuard><DevCoursePlayer /></DevPlayerGuard>} />

{/* SPECIFIC COURSE PLAYERS (must be before catch-alls) */}
<Route path="/learn/pmp-foundations-exam-overview" element={<Suspense fallback={<div>Loading...</div>}><PMPFoundationsPlayer /></Suspense>} />

{/* GENERIC COURSE PLAYERS (catch-alls - must be last) */}
<Route path="/learn/:slug/player" element={<MiniLessonPlayer />} />
<Route path="/learn/:slug/module/:moduleKey" element={<CpmaiModulePlayer />} />
<Route path="/learn/:slug" element={<LearnCoursePage />} />

{/* EXAMS - More specific routes MUST come before catch-all :attemptId */}
{/* CAPM, CPMAI and CompTIA practice exams are offline while they are rebuilt */}
<Route path="/learn/exams/cpmai-practice" element={<Navigate to="/products?tab=exams" replace />} />
<Route path="/learn/exams/cpmai-practice/:attemptId" element={<Navigate to="/products?tab=exams" replace />} />
<Route path="/learn/exams/comptia-practice" element={<Navigate to="/products?tab=exams" replace />} />
<Route path="/learn/exams/comptia-practice/:attemptId" element={<Navigate to="/products?tab=exams" replace />} />
<Route path="/learn/exams/pmp-practice" element={<Suspense fallback={<div>Loading…</div>}><PmpPracticeExamVolume1 /></Suspense>} />
<Route path="/learn/exams/pmp-practice/:attemptId" element={<Suspense fallback={<div>Loading…</div>}><PmpPracticeExamVolume1 /></Suspense>} />
<Route path="/learn/exams/capm-practice" element={<Navigate to="/products?tab=exams" replace />} />
<Route path="/learn/exams/capm-practice/:attemptId" element={<Navigate to="/products?tab=exams" replace />} />
<Route path="/learn/exams/pmp-scenario/results/:attemptId" element={<Suspense fallback={<div>Loading…</div>}><PmpScenarioResults /></Suspense>} />
<Route path="/learn/exams/pmp-scenario/review/:attemptId" element={<Suspense fallback={<div>Loading…</div>}><PmpScenarioReview /></Suspense>} />
<Route path="/learn/exams/pmp-scenario/:attemptId" element={<Suspense fallback={<div>Loading…</div>}><PmpScenarioIntelligenceExam /></Suspense>} />

{/* PM Simulator - free for all authenticated users */}
<Route path="/learn/pm-simulator" element={<PMSimulator />} />
<Route path="/learn/simulator/pm" element={<Navigate to="/learn/pm-simulator" replace />} />
<Route path="/learn/pm101/sim" element={<Navigate to="/learn/pm-simulator" replace />} />
<Route path="/learn/simulator/pmo-dashboard" element={<Navigate to="/learn/pm-simulator" replace />} />
<Route path="/learner/course-progress/:courseId" element={<LearnerCourseProgress />} />
<Route path="/learner/my-progress" element={<MyProgress />} />
                <Route path="/learner/badge-earned" element={<BadgeCongratulations />} />

{/* SETTINGS */}
<Route path="/settings/notifications" element={<NotificationSettings />} />

{/* Legacy learner route redirects */}
<Route path="/learner/pmp-course" element={<Navigate to="/academy/my/pmbok8-exam-prep" replace />} />
<Route path="/learner/pmp-course/final-exam" element={<Navigate to="/academy/my/pmbok8-exam-prep/final-exam" replace />} />
<Route path="/learner/pmp-course/certificate" element={<Navigate to="/academy/my/pmbok8-exam-prep/certificate" replace />} />
<Route path="/learner/pmp-course/*" element={<Navigate to="/academy/my/pmbok8-exam-prep" replace />} />
<Route path="/learner/prompt-course" element={<Navigate to="/academy/my/prompt-engineering" replace />} />
<Route path="/learner/prompt-course/certificate" element={<Navigate to="/academy/my/prompt-engineering/certificate" replace />} />
<Route path="/learner/prompt-course/:moduleId" element={<RedirectPromptModule />} />
<Route path="/learner/ai-delivery-course" element={<Navigate to="/academy/my/ai-systems-delivery" replace />} />
<Route path="/learner/ai-delivery-course/certificate" element={<Navigate to="/academy/my/ai-systems-delivery/certificate" replace />} />
<Route path="/learner/ai-delivery-course/:moduleId" element={<RedirectAIDeliveryModule />} />
<Route path="/learn/ai-course/:slug" element={<RedirectAIPMCourse />} />
<Route path="/learn/ai-course/:slug/introduction" element={<RedirectAIPMIntro />} />
<Route path="/learn/ai-course/:slug/module/:moduleIndex" element={<RedirectAIPMModuleRedir />} />
<Route path="/learn/ai-course/:slug/certificate" element={<RedirectAIPMCert />} />
<Route path="/learn/project-management-101/player" element={<Navigate to="/academy/my/project-management-101" replace />} />
<Route path="/learn/ai-for-project-managers" element={<Navigate to="/academy/my/ai-for-pms" replace />} />
<Route path="/learn/ai-for-pms" element={<Navigate to="/academy/my/ai-for-pms" replace />} />

</Route> {/* <--- THIS IS THE CRITICAL CLOSING TAG FOR REQUIREROLES */}

            </Route> {/* CLOSE DashboardLayout */}

            {/* ==================== FALLBACK ==================== */}
            {/* Outside the signed-in layout so visitors who aren't signed in see it too
                (it redirects to the closest real page instead of sending them to login). */}
            <Route element={<HomePageLayout />}>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
        </AdminSubdomainGuard>
      </IdleSignout>
    </Router>
    </ErrorBoundary>
  );
}
